import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Rodal from 'rodal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchUsers } from '../../services/user_services';
import InputSelect from '../../components/InputSelect';
import { userLabel } from './appropriationUtils';

const SELECT_STEP = 'SELECT';
const CONFIRM_STEP = 'CONFIRM';

const TransferAppropriationModal = ({
  visible,
  appropriationOwner,
  loading,
  onCancel,
  onConfirm,
  entityLabel,
  fetchUsersApi,
}) => {
  const [step, setStep] = useState(SELECT_STEP);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const appropriationOwnerId = appropriationOwner && appropriationOwner.id;

  const loadUserOptions = useCallback(
    async (term) => {
      setLoadingUsers(true);
      try {
        // Regra: apenas usuários marcados como "Membro de equipe" podem receber
        // a apropriação. O dono atual é removido da lista.
        const data = await fetchUsersApi(term);
        setUsers((data.results || []).filter(u => u.id !== appropriationOwnerId));
      } catch (error) {
        toast.error('Não foi possível carregar os usuários. Tente novamente.');
      } finally {
        setLoadingUsers(false);
      }
    },
    [appropriationOwnerId, fetchUsersApi]
  );

  useEffect(() => {
    if (!visible) return;

    setStep(SELECT_STEP);
    setSearch('');
    setSelectedUser(null);
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;

    const timeout = setTimeout(() => loadUserOptions(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [visible, search, loadUserOptions]);

  const handleConfirm = () => {
    if (step === SELECT_STEP) {
      setStep(CONFIRM_STEP);
      return;
    }

    onConfirm(selectedUser);
  };

  const handleBack = () => {
    if (step === CONFIRM_STEP) {
      setStep(SELECT_STEP);
      return;
    }

    onCancel();
  };

  return (
    <Rodal
      visible={visible}
      onClose={loading ? () => {} : onCancel}
      closeOnEsc={!loading}
      width={520}
      height={440}
      showCloseButton={!loading}
    >
      <div className="d-flex flex-column h-100">
        <div className="modal-header">
          <div className="text-center">
            <h2>Transferir Apropriação</h2>
          </div>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {step === SELECT_STEP ? (
            <>
              <p>
                Selecione o membro de equipe que assumirá a apropriação desta{' '}
                {entityLabel}.
              </p>
              <InputSelect
                value={selectedUser}
                onChange={value => setSelectedUser(value)}
                onInputChange={(value, { action }) => {
                  if (action === 'input-change') setSearch(value);
                }}
                options={users}
                isLoading={loadingUsers}
                placeholder="Membro de equipe"
                className="w-100 mb-2"
                getOptionLabel={userLabel}
                getOptionValue={user => user.id}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                }}
              />
            </>
          ) : (
            <p>
              Tem certeza que deseja transferir esta apropriação para{' '}
              <strong>{selectedUser && userLabel(selectedUser)}</strong>? A
              apropriação atual será encerrada e o registro ficará no histórico.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            disabled={loading}
            onClick={handleBack}
            className="btn-danger btn blue"
          >
            {step === CONFIRM_STEP ? 'Voltar' : 'Cancelar'}
          </button>
          <button
            type="button"
            disabled={loading || !selectedUser}
            onClick={handleConfirm}
            className="btn-primary btn blue"
          >
            {loading ? (
              <FontAwesomeIcon icon="spinner" spin />
            ) : (
              step === CONFIRM_STEP ? 'Confirmar' : 'Continuar'
            )}
          </button>
        </div>
      </div>
    </Rodal>
  );
};

TransferAppropriationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  // Usuário que detém a apropriação (não necessariamente quem está logado:
  // um superusuário pode transferir em nome de outra pessoa).
  appropriationOwner: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
    last_name: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  entityLabel: PropTypes.string,
  fetchUsersApi: PropTypes.func,
};

TransferAppropriationModal.defaultProps = {
  appropriationOwner: null,
  loading: false,
  entityLabel: 'matrícula',
  fetchUsersApi: term => fetchUsers(term, { is_staff: true }),
};

export default TransferAppropriationModal;
